import {
  defineAsyncMicroApp,
  unmountApp,
} from '@gohighlevel/ghl-module-federation-plugin'
import AppState from '@/states/app'

let calendarIntegrationApp: any = {}
let calendarScheduleApp: any = {}

function useAppContext() {
  function mountAppContext(apps: any) {
    for (const iterator in apps) {
      window[iterator] = apps[iterator]
    }
  }
  return { mountAppContext }
}

export async function mountCalendarSettingsApp(
  mountId: string,
  configuration: any
) {
  if (calendarIntegrationApp?.unmount) {
    calendarIntegrationApp.unmount()
  }
  if (calendarScheduleApp?.unmount) {
    calendarScheduleApp.unmount()
  }

  const { mountAppContext } = useAppContext()
  let Component
  switch (mountId) {
    case 'CalendarIntegration':
      Component = 'CalendarIntegration'
      break
    case 'CalendarSchedule':
      Component = 'CalendarSchedule'
      break
    default:
      Component = null
  }

  const [createApp, mountApp] = await defineAsyncMicroApp(
    'calendarSettingsApp',
    ['vue3', Component]
  )

  const calendarSettingsApp = createApp(mountApp, {
    ...configuration,
  })
  mountAppContext({ calendarSettingsAppInstance: calendarSettingsApp })
  calendarSettingsApp.mount(`#${mountId}`)

  if (mountId === 'CalendarIntegration') {
    calendarIntegrationApp = calendarSettingsApp
  } else if (mountId === 'CalendarSchedule') {
    calendarScheduleApp = calendarSettingsApp
  }
}

export function unmountCalendarSettingsApp(mountId: string) {
  let appToUnmount

  switch (mountId) {
    case 'CalendarIntegration':
      appToUnmount = calendarIntegrationApp
      break
    case 'CalendarSchedule':
      appToUnmount = calendarScheduleApp
      break
    default:
      break
  }
  if (appToUnmount?.unmount) {
    appToUnmount.unmount()
    unmountApp('calendarSettingsApp')
  }
}

let calendarMeetingsApp: any = {}
// Bumped by every mount request and by every unmount, so a request that is still awaiting the remote
// can tell it has been superseded. Without it a slow load resolves after the host closed the view and
// mounts into a container that no longer exists (Vue silently no-ops) or wipes a newer mount.
let calendarMeetingsMountToken = 0

// Held so a prefetch and the mount that follows share one download, and a click landing mid-download
// joins it instead of starting a second.
let calendarMeetingsLoad: Promise<any[]> | null = null

const loadCalendarMeetingsRemote = (): Promise<any[]> => {
  if (!calendarMeetingsLoad) {
    calendarMeetingsLoad = defineAsyncMicroApp('calendarMeetingsApp', ['vue3', 'CalendarMeetings']).catch(error => {
      // Drop the rejected promise so a later mount retries instead of replaying the failure.
      calendarMeetingsLoad = null
      throw error
    })
  }
  return calendarMeetingsLoad
}

// Fire-and-forget: mountCalendarMeetings retries and reports properly if the user does click.
export function prefetchCalendarMeetings(): void {
  void loadCalendarMeetingsRemote().catch(() => {})
}

export async function mountCalendarMeetings(mountId: string, configuration: any) {
  const token = ++calendarMeetingsMountToken

  if (calendarMeetingsApp?.unmount) {
    calendarMeetingsApp.unmount()
    calendarMeetingsApp = {}
  }

  const { mountAppContext } = useAppContext()

  const [createApp, mountApp] = await loadCalendarMeetingsRemote()

  if (token !== calendarMeetingsMountToken) {
    return
  }

  const meetingsInstance = createApp(mountApp, {
    ...configuration,
  })

  mountAppContext({ calendarMeetingsAppInstance: meetingsInstance })
  meetingsInstance.mount(`#${mountId}`)

  calendarMeetingsApp = meetingsInstance
}

export function unmountCalendarMeetings(_mountId: string) {
  // Invalidate any in-flight mount first, even when there is nothing mounted yet.
  calendarMeetingsMountToken++

  if (calendarMeetingsApp?.unmount) {
    calendarMeetingsApp.unmount()
    unmountApp('calendarMeetingsApp')
    calendarMeetingsApp = {}
  }
}

let calendarTypePickerApp: any = {}
let calendarTypePickerMountToken = 0

// Calendar-type picker island — a second module exposed from the SAME calendarMeetingsApp remote, mounted
// as a small dropdown under the "+ New Calendar" button. Selecting a preset tears this down and mounts
// the meetings in create mode from the same container.
export async function mountCalendarTypePicker(mountId: string, configuration: any) {
  const token = ++calendarTypePickerMountToken

  if (calendarTypePickerApp?.unmount) {
    calendarTypePickerApp.unmount()
    calendarTypePickerApp = {}
  }

  const { mountAppContext } = useAppContext()

  const [createApp, mountApp] = await defineAsyncMicroApp('calendarMeetingsApp', ['vue3', 'CalendarTypePicker'])

  // Dropped if the picker was closed (or reopened) while the remote was loading.
  if (token !== calendarTypePickerMountToken) {
    return
  }

  const pickerInstance = createApp(mountApp, {
    ...configuration,
  })

  mountAppContext({ calendarTypePickerAppInstance: pickerInstance })
  pickerInstance.mount(`#${mountId}`)

  calendarTypePickerApp = pickerInstance
}

export function unmountCalendarTypePicker(_mountId: string) {
  calendarTypePickerMountToken++

  if (calendarTypePickerApp?.unmount) {
    calendarTypePickerApp.unmount()
    // Do NOT unmountApp('calendarMeetingsApp') here: the picker shares the container with the meetings, which
    // mounts from it immediately after a selection; nulling the container global would force a needless
    // reload. The container is only torn down when leaving the meetings (unmountCalendarMeetings).
    calendarTypePickerApp = {}
  }
}

// Meeting status settings — a separate module exposed from the SAME calendarMeetingsApp remote, mounted into
// the Appointment Status tab of calendar preferences. It keeps its own instance and mount token so mounting
// here never tears down the meetings view, which shares the remote container.
let meetingStatusSettingsApp: any = {}
let meetingStatusSettingsMountToken = 0

export async function mountMeetingStatusSettings(mountId: string, configuration: any) {
  const token = ++meetingStatusSettingsMountToken

  if (meetingStatusSettingsApp?.unmount) {
    meetingStatusSettingsApp.unmount()
    meetingStatusSettingsApp = {}
  }

  const { mountAppContext } = useAppContext()

  const [createApp, mountApp] = await defineAsyncMicroApp('calendarMeetingsApp', ['vue3', 'StatusSettings'])

  // Dropped if the tab was left (or re-entered) while the remote was loading.
  if (token !== meetingStatusSettingsMountToken) {
    return
  }

  const statusSettingsInstance = createApp(mountApp, {
    ...configuration,
  })

  mountAppContext({
    meetingStatusSettingsAppInstance: statusSettingsInstance,
    // The meetings app reads its auth token off window.getToken().
    getToken: async () => AppState.tokenId,
  })
  statusSettingsInstance.mount(`#${mountId}`)

  meetingStatusSettingsApp = statusSettingsInstance
}

export function unmountMeetingStatusSettings() {
  // Invalidate any in-flight mount first, even when there is nothing mounted yet.
  meetingStatusSettingsMountToken++

  if (meetingStatusSettingsApp?.unmount) {
    meetingStatusSettingsApp.unmount()
    // Do NOT unmountApp('calendarMeetingsApp') here: the meetings view shares that container and caches the
    // factories it resolved from it, so nulling the container global on a tab switch would leave it stale.
    meetingStatusSettingsApp = {}
  }
}
